/**
 * Petals P2P - Paylasimli GPU network
 * Kullanici GPU'sunun bir kismini paylasiyor, buyuk modellere erisim kazaniyor
 */

import axios from 'axios'
import { AlertCircle, Check, Network, Play, Square } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface PetalsStatus {
  is_connected: boolean
  node_id: string | null
  gpu_share_percent: number
  model: string | null
  peers_count: number
  tokens_earned: number
  uptime_hours: number
}

export default function PetalsPage() {
  const [status, setStatus] = useState<PetalsStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [gpuShare, setGpuShare] = useState(20)

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/aicloud/petals/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = resp.data as PetalsStatus
      setStatus(data)
      setGpuShare(data.gpu_share_percent || 20)
    } catch {
      setStatus({ is_connected: false, node_id: null, gpu_share_percent: 20, model: null, peers_count: 0, tokens_earned: 0, uptime_hours: 0 })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchStatus() }, [])

  const handleToggle = async () => {
    setToggling(true); setError(null)
    try {
      const token = localStorage.getItem('token')
      if (status?.is_connected) {
        await axios.post(`${API_URL}/api/aicloud/petals/disconnect`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Disconnected from Petals network')
      } else {
        await axios.post(`${API_URL}/api/aicloud/petals/connect`, { gpu_share_percent: gpuShare }, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Connected to Petals network')
      }
      void fetchStatus()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Operation failed')
    } finally {
      setToggling(false)
    }
  }

  if (loading) {return <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>}

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Petals P2P</h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          Share your GPU, access large models (70B+) at near-zero cost via distributed inference.
        </p>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '16px' }}>
          <Check size={14} /> {success}
        </div>
      )}

      {/* Status kart */}
      <div style={{ padding: '24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <Network size={20} color={status?.is_connected ? '#10b981' : '#6b7280'} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              {status?.is_connected ? 'Connected to Petals Network' : 'Not Connected'}
            </div>
            {status?.node_id && (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                Node: {status.node_id}
              </div>
            )}
          </div>
          <div style={{ marginLeft: 'auto', width: '10px', height: '10px', borderRadius: '50%', background: status?.is_connected ? '#10b981' : '#6b7280' }} />
        </div>

        {/* Stats */}
        {status?.is_connected && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
            {[
              { label: 'Peers', value: status.peers_count },
              { label: 'FLZ Earned', value: status.tokens_earned },
              { label: 'Uptime (h)', value: status.uptime_hours },
            ].map(s => (
              <div key={s.label} style={{ padding: '12px', background: 'var(--body-bg)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* GPU share slider */}
        {!status?.is_connected && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              GPU Share: {gpuShare}%
            </label>
            <input
              type="range" min={10} max={80} step={5}
              value={gpuShare}
              onChange={e => setGpuShare(Number(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)' }}>
              <span>10% (minimal)</span><span>80% (maximum)</span>
            </div>
          </div>
        )}

        <button
          onClick={() => { void handleToggle() }}
          disabled={toggling}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 20px',
            background: status?.is_connected ? '#ef4444' : '#10b981',
            color: 'white', border: 'none', borderRadius: '8px',
            cursor: toggling ? 'not-allowed' : 'pointer',
            fontSize: '13px', fontWeight: 600, opacity: toggling ? 0.6 : 1
          }}
        >
          {status?.is_connected ? <><Square size={14} /> Disconnect</> : <><Play size={14} /> Connect & Share GPU</>}
        </button>
      </div>

      {/* Bilgi */}
      <div style={{ padding: '16px', background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
        Share {gpuShare}% of your GPU → Access Llama 70B, Bloom 176B and other large models.
        Earn FLZ tokens for every hour you contribute to the network.
      </div>
    </div>
  )
}
